import { ApolloQueryResult } from '@apollo/client';
import { IProps } from '@components/messages/types';
import React, { useEffect, useState } from 'react';
import { ITableActions } from 'apps/things/app/item/context/ItemProvider';

import {
  GetAllEvents,
  GetAllEventsVariables,
  GetAllEvents_getAllEvents_page_edges,
} from '../types/GetAllEvents';
import { useLazyGetAllEventsQuery } from '../queries';
import { PAGE_LIST_LIMIT } from '@constants';
import useOVSPagination from 'hooks/useOVSPagination';

export interface IEventContext {
  items: GetAllEvents_getAllEvents_page_edges[];
  setItems: (item: GetAllEvents_getAllEvents_page_edges[]) => void;
  loading: boolean;
  getItems: () => void;
  activeStage: number;
  setActiveStage: (stage: number) => void;
  tableActions: ITableActions[];
  refetchItems:
    | ((
        variables?: Partial<GetAllEventsVariables> | undefined
      ) => Promise<ApolloQueryResult<GetAllEvents>>)
    | undefined;
  goTo: (nextPrev: boolean) => void;
  paging: any;
}

export const EventContext = React.createContext<IEventContext>(
  {} as IEventContext
);

const EventProvider: React.FC<IProps> = ({ children }) => {
  const [activeStage, setActiveStage] = useState(0);
  const [item, setItem] = React.useState(
    {} as GetAllEvents_getAllEvents_page_edges
  );
  const [items, setItems] = React.useState<
    GetAllEvents_getAllEvents_page_edges[]
  >([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  /// hooks
  const [
    getItems,
    { refetch: refetchItems, data, loading, fetchMore },
  ] = useLazyGetAllEventsQuery({
    first: PAGE_LIST_LIMIT,
    filters: {},
  });

  const {
    endCursorStack,
    initPagAction,
    setPaging,
    paging,
  } = useOVSPagination();

  useEffect(() => {
    setItems(data?.getAllEvents?.page?.edges || []);
    setPaging({
      ...data?.getAllEvents?.pageData,
      ...data?.getAllEvents?.page?.pageInfo,
    }); // eslint-disable-next-line
  }, [data]);

  const goTo = async (next = true) => {
    if (isLoadingMore) return;

    let variables: GetAllEventsVariables = {
      first: PAGE_LIST_LIMIT,
      filters: {},
      ...initPagAction(next),
    };

    if (fetchMore) {
      setIsLoadingMore(true);
      const _data = await fetchMore({
        variables,
        updateQuery: (previousResult, { fetchMoreResult }) => {
          setIsLoadingMore(false);
          if (!fetchMoreResult) {
            return previousResult;
          }
          return {
            ...fetchMoreResult,
          };
        },
      });

      setItems(_data?.data?.getAllEvents?.page?.edges || []);

      setPaging({
        ..._data?.data?.getAllEvents?.pageData,
        ..._data?.data?.getAllEvents?.page?.pageInfo,
        hasPreviousPage: endCursorStack.length > 0,
      });
    }
  };

  const value = React.useMemo(
    () => ({
      item,
      setItem,
      items,
      setItems,
      goTo,
      paging,
      loading: loading,
      getItems: getItems,
      refetchItems,
      tableActions: [],
      activeStage,
      setActiveStage,
    }), // eslint-disable-next-line
    [items, loading, paging]
  );

  return (
    <EventContext.Provider value={value}>{children}</EventContext.Provider>
  );
};

export default EventProvider;
